import { createSlice } from '@reduxjs/toolkit';
import { sendChatMessageThunk } from '../thunks/sendChatMessage';
import { fetchChatMessagesThunk } from '../thunks/fetchChatMessages';
import { getImageUrlsThunk } from '../thunks/getImageUrls';
import { handleWebsocketMessageThunk } from '../thunks/handleWebsocketMessage';
import { getProjectThreadThunk } from '../thunks/getProjectThread';
import { getProjectThreadsThunk } from '../thunks/getProjectThreads';
import { loadThreadOnDemand } from '../thunks/loadThreadOnDemand';
import { getLastReadMessagesForThreadThunk } from '../thunks/getLastReadMessagesForThread';
import { setReadBy } from '../utils/setReadBy';
import { getMessageReadTimeThunk } from '../thunks/getMessageReadTime';
import { getPrivateThreadThunk } from '../thunks/getPrivateThread';
import { getPrivateThreadsThunk } from '../thunks/getPrivateThreads';

export const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    allMessagesFetched: false,
    allUsers: {},
    isChatDialogOpen: false,
    lastEvaluatedKey: null,
    lastReadMessage: null,
    lastReadMessages: [],
    loading: true,
    messages: [],
    needImageUrls: false,
    newVideoChatSessionStarted: false,
    notifyMessage: null,
    openedAttachment: null,
    project: null,
    projectUsers: {},
    thread: null,
    threads: {},
    threadsByProject: {},
    unreadChatMessagesCount: 0,
    users: {},
    videoChatOpen: false,
    videoChatSession: null,
    chatWebsocket: null,
    subThreads: [],
  },
  reducers: {
    addMessage: (state, action) => {
      const message = action.payload;
      if (message.isAttachment) {
        const attachedTo = state.messages.find(
          (m) => m.id === message.attachedTo
        );
        if (attachedTo) {
          // Carry the blob preview URL into the attachment object so it can
          // be used as a fallback until the real signed URL is ready.
          if (attachedTo.imagePreview) {
            message.imagePreview = attachedTo.imagePreview;
          }
          attachedTo.attachments = [message];
          delete attachedTo.imagePreview;
          delete attachedTo.attachmentPreviewName;
          state.needImageUrls = true;
        }
      } else {
        // Check if message already exists to avoid duplicates
        const messageExists =
          message.id && state.messages.some((m) => m.id === message.id);
        if (!messageExists) {
          state.messages = [message, ...state.messages];
          if (state.isChatDialogOpen) {
            state.lastReadMessage = message;
          }
        }
      }
    },
    addMessagesToStart: async (state, action) => {
      const { messages } = action.payload;
      const nonAttachmentMessages = messages
        .filter((message) => !message.isAttachment)
        .map((message) => setReadBy(message, state));
      const attachments = messages.filter(
        (message) => message.isAttachment && message.attachedTo
      );
      const newMessages = [...state.messages, ...nonAttachmentMessages];
      for (const attachment of attachments) {
        const attachedTo = newMessages.find(
          (message) => message.id === attachment.attachedTo
        );
        if (attachedTo) {
          attachedTo.attachments = [attachment];
          delete attachedTo.imagePreview;
          delete attachedTo.attachmentPreviewName;
        }
      }

      state.messages = newMessages;
    },
    addUnreadMessageToThread: (state, action) => {
      const { projectId, originalThreadId, childThread, threadId } = action;

      const threadByProject = state.threadsByProject[projectId];
      const privateThread = state.threads[threadId];

      if (!threadByProject && !privateThread) {
        // Thread doesn't exist in state - this shouldn't happen anymore
        // but we return gracefully to avoid errors
        return;
      }
      if (childThread) {
        if (threadByProject) {
          threadByProject.unreadMessagesInChildProjects++;
          const childThreadInProject = threadByProject.children.find(
            (child) => child.id === originalThreadId
          );
          if (childThreadInProject) {
            childThreadInProject.unreadMessagesCount++;
          }
        }
      } else {
        if (threadByProject) threadByProject.unreadMessagesCount++;
        if (privateThread) privateThread.unreadMessagesCount++;
      }

      const parentProjectId = threadByProject?.parent;
      if (parentProjectId) {
        try {
          chatSlice.caseReducers.addUnreadMessageToThread(state, {
            projectId: parentProjectId,
            originalThreadId:
              originalThreadId ||
              threadByProject.threadId ||
              threadByProject.id,
            childThread: threadByProject,
            threadId: threadByProject.threadId || threadByProject.id,
          });
        } catch (error) {}
      }
    },
    setAllMessagesFetched: (state, action) => {
      state.allMessagesFetched = true;
    },
    setAllMessagesRead: (state, action) => {
      const {
        projectId,
        originalThreadId,
        childThread,
        messagesReadCount,
        thread,
      } = action;
      const threadByProject = state.threadsByProject[projectId];

      const privateThread =
        (thread?.threadId || thread?.id) &&
        state.threads[thread.threadId || thread.id];

      if (!threadByProject && !privateThread) {
        return;
      }
      if (childThread) {
        threadByProject.unreadMessagesInChildProjects -= messagesReadCount || 0;
        threadByProject.children.find(
          (child) => child.id === originalThreadId
        ).unreadMessagesCount = 0;
      } else {
        if (threadByProject) {
          threadByProject.unreadMessagesCount = 0;
        }
      }

      if (privateThread) {
        privateThread.unreadMessagesCount = 0;
      }

      const parentProjectId = threadByProject?.parent;
      if (parentProjectId) {
        try {
          chatSlice.caseReducers.setAllMessagesRead(state, {
            projectId: parentProjectId,
            originalThreadId:
              originalThreadId ||
              threadByProject.threadId ||
              threadByProject.id,
            childThread: threadByProject,
            messagesReadCount,
            thread: threadByProject,
          });
        } catch {}
      }
    },
    setChatDialogOpen: (state, action) => {
      state.isChatDialogOpen = action.payload;
      if (
        state.isChatDialogOpen &&
        state.messages &&
        state.messages.length > 0 &&
        state.thread
      ) {
        state.lastReadMessage = state.messages[0];
        chatSlice.caseReducers.setAllMessagesRead(state, {
          projectId: state.thread.projectId,
          messagesReadCount: state.unreadChatMessagesCount,
          thread: state.thread,
        });
      }
      // Clear notification when opening chat dialog
      if (state.isChatDialogOpen) {
        state.notifyMessage = null;
      }
    },
    setLastEvaluatedKey: (state, action) => {
      const lastEvaluatedKey = action.payload;
      state.lastEvaluatedKey = lastEvaluatedKey;
    },
    setNewVideoChatSessionStarted: (state, action) => {
      state.newVideoChatSessionStarted = action.payload;
    },
    setNotifyMessage: (state, action) => {
      state.notifyMessage = action.payload;
    },
    setOpenedAttachment: (state, action) => {
      const attachment = action.payload;
      state.openedAttachment = attachment;
    },
    setProject: (state, action) => {
      const project = action.payload;

      if (project?.id !== state.project?.id) {
        state.project = project;
      }
    },
    setThread: (state, action) => {
      const thread = action.payload;
      if (thread?.id !== state.thread?.id) {
        state.thread = thread;
        state.messages = [];
        state.lastEvaluatedKey = null;
        state.allMessagesFetched = false;
        state.loading = true;
        state.lastReadMessage = null;
        state.unreadChatMessagesCount = thread?.unreadMessagesCount || 0;
        state.videoChatSession = null;
        state.subThreads = [];
        if (thread?.userIds) {
          const filteredUsers = {};
          for (const userId of thread.userIds) {
            if (state.allUsers[userId]) {
              filteredUsers[userId] = state.allUsers[userId];
            }
          }
          state.users = filteredUsers;
        } else {
          state.users = state.projectUsers;
        }
        // Clear notification when opening a thread
        state.notifyMessage = null;
      }
    },
    setVideoChatOpen: (state, action) => {
      state.videoChatOpen = action.payload;
      if (!state.videoChatOpen && state.messages.length > 0 && state.thread) {
        state.lastReadMessage = state.messages[0];
        chatSlice.caseReducers.setAllMessagesRead(state, {
          projectId: state.thread.projectId,
          messagesReadCount: state.unreadChatMessagesCount,
        });
      }
    },
    setVideoChatSession: (state, action) => {
      state.videoChatSession = action.payload;
    },
    setChatWebsocket: (state, action) => {
      state.chatWebsocket = action.payload;
    },
    addSubThread: (state, action) => {
      const newThread = action.payload;
      state.subThreads = [...state.subThreads, newThread];
      state.threads[newThread.threadId || newThread.id] = newThread;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(handleWebsocketMessageThunk.fulfilled, (state, action) => {
        const { message, threadWasFetched } = action.payload;
        const messageSender = message?.userId;

        const { user } = action.meta.arg;
        if (message) {
          const messageType = message.type.split('#')[0];
          if (messageType === 'MESSAGE' && messageSender === user?.id) {
            // Own message confirmed by WebSocket — update the optimistic entry
            const isCurrentThreadMessage =
              message.threadId === (state.thread?.threadId || state.thread?.id);
            if (isCurrentThreadMessage) {
              if (message.isAttachment && message.attachedTo) {
                // Attachment: link to parent (parent already has the real id
                // from a prior WS echo of the text message)
                chatSlice.caseReducers.addMessage(state, { payload: message });
              }
            }
          } else if (messageType === 'MESSAGE' && messageSender !== user?.id) {
            const isCurrentThreadMessage =
              message.threadId === (state.thread?.threadId || state.thread?.id);
            if (isCurrentThreadMessage) {
              chatSlice.caseReducers.addMessage(state, { payload: message });
            }
            if (
              !message.isAttachment &&
              (!isCurrentThreadMessage || !state.isChatDialogOpen)
            ) {
              // Only increment unread count if we didn't just fetch the thread
              // (fetched threads already have the correct count from backend)
              if (!threadWasFetched) {
                chatSlice.caseReducers.addUnreadMessageToThread(state, {
                  projectId: message.projectId,
                  threadId: message.threadId,
                });
              }
              state.notifyMessage = message;
            }
          } else if (
            messageType === 'LAST_READ_MESSAGE' &&
            messageSender !== user?.id
          ) {
            const { threadId, userId, updatedAt } = message;
            if (threadId === state.thread?.id) {
              let previousLastReadTime = null;
              let messagesToUpdate = [];
              const lastReadMessageItem = state.lastReadMessages.find(
                (item) => item.userId === userId
              );
              if (lastReadMessageItem) {
                previousLastReadTime = lastReadMessageItem.updatedAt;
                messagesToUpdate = state.messages.filter(
                  (m) =>
                    (!m.createdAt ||
                      (m.createdAt > previousLastReadTime &&
                        m.createdAt <= updatedAt)) &&
                    m.userId !== userId
                );
                const index =
                  state.lastReadMessages.indexOf(lastReadMessageItem);
                state.lastReadMessages[index] = {
                  ...lastReadMessageItem,
                  ...message,
                };
              } else {
                state.lastReadMessages.push(message);
                messagesToUpdate = state.messages.filter(
                  (m) => m.createdAt <= updatedAt && m.userId !== userId
                );
              }
              for (const messageToUpdate of messagesToUpdate) {
                if (!messageToUpdate.readBy) {
                  messageToUpdate.readBy = [];
                }
                const readByEntry = messageToUpdate.readBy.find(
                  (u) => u.userId === userId
                );
                if (readByEntry) {
                  readByEntry.readAt = updatedAt;
                } else {
                  messageToUpdate.readBy.push({
                    userId: userId,
                    firstName: lastReadMessageItem?.firstName || '',
                    lastName: lastReadMessageItem?.lastName || '',
                    readAt: updatedAt,
                  });
                }
              }
            }
          } else if (messageType === 'VIDEOCHAT_SESSION') {
            const { threadId, isActive, directRecipient } = message;

            if (isActive && directRecipient) {
              state.notifyMessage = message;
            }
            if (threadId === (state.thread?.threadId || state.thread?.id)) {
              if (
                isActive &&
                !state.videoChatSession?.isActive &&
                messageSender !== user?.id
              ) {
                state.newVideoChatSessionStarted = true;
              }
              state.videoChatSession = message;
            }
          }
        }
      })
      .addCase(loadThreadOnDemand.fulfilled, (state, action) => {
        const thread = action.payload;
        const threadsByProject = {
          ...state.threadsByProject,
        };
        threadsByProject[thread.projectId] = thread;
        state.threads[thread.threadId || thread.id] = thread;
        state.threadsByProject = threadsByProject;
      })
      .addCase(sendChatMessageThunk.pending, (state, action) => {
        const { message, attachment } = action.meta.arg;
        if (!message.attachedTo) {
          if (attachment) {
            const objectUrl = URL.createObjectURL(attachment);
            message.imagePreview = objectUrl;
            message.attachmentPreviewName = attachment.name;
          }
          const messageExists =
            message.id && state.messages.some((m) => m.id === message.id);
          if (!messageExists) {
            state.messages = [message, ...state.messages];
          }
        }
      })
      .addCase(fetchChatMessagesThunk.fulfilled, (state, action) => {
        const { limit } = action.meta.arg;
        const { messages } = action.payload;
        const previousMessages = state.messages || [];
        chatSlice.caseReducers.addMessagesToStart(state, action);
        state.loading = false;

        if (messages.length < limit) {
          state.allMessagesFetched = true;
        }
        if (messages.length > 0) {
          const lastMessage = messages.at(-1);
          const key = {
            id: lastMessage.id,
            target: lastMessage.target,
            type: lastMessage.type,
          };
          state.lastEvaluatedKey = key;
          state.needImageUrls = true;
          if (
            previousMessages.length === 0 &&
            state.isChatDialogOpen &&
            !state.videoChatOpen
          ) {
            state.lastReadMessage = messages[0];
            chatSlice.caseReducers.setAllMessagesRead(state, {
              projectId: state.thread.projectId,
              messagesReadCount: state.unreadChatMessagesCount,
              thread: state.thread,
            });
          }
        }
      })
      .addCase(getImageUrlsThunk.fulfilled, (state, action) => {
        const { messagesWithAttachments } = action.payload;
        const messages = state.messages;
        for (const messageWithAttachments of messagesWithAttachments) {
          const message = messages.find(
            (m) => m.id === messageWithAttachments.id
          );
          if (message) {
            message.attachments = messageWithAttachments.attachments;
          }
        }
        state.messages = messages;
        state.needImageUrls = false;
      })
      .addCase(getProjectThreadThunk.fulfilled, (state, action) => {
        const thread = action.payload;
        const { projectId } = action.meta.arg;
        const threadsByProject = {
          ...state.threadsByProject,
        };
        threadsByProject[projectId] = thread;
        state.threadsByProject = threadsByProject;
        state.threads[thread.threadId || thread.id] = thread;
        if (projectId === state.project?.id) {
          chatSlice.caseReducers.setThread(state, action);
        }
      })
      .addCase(getProjectThreadsThunk.fulfilled, (state, action) => {
        const { threadsByProject, threads } = action.payload;
        state.threadsByProject = threadsByProject;
        state.threads = { ...state.threads, ...threads };
      })
      .addCase(getPrivateThreadThunk.fulfilled, (state, action) => {
        const { thread, setAsActive } = action.payload;
        if (thread?.id) {
          // Only set as active thread if explicitly requested
          if (setAsActive) {
            chatSlice.caseReducers.setThread(state, { payload: thread });
            chatSlice.caseReducers.setProject(state, { payload: null });
          }
          state.threads[thread.threadId || thread.id] = thread;
        }
      })
      .addCase(getPrivateThreadsThunk.fulfilled, (state, action) => {
        const threads = action.payload;
        state.threads = { ...state.threads, ...threads };
      })
      .addCase(getLastReadMessagesForThreadThunk.fulfilled, (state, action) => {
        const lastReadMessages = action.payload;
        state.lastReadMessages = lastReadMessages;
        const updatedMessages = state.messages.map((message) =>
          setReadBy(message, state)
        );
        state.messages = updatedMessages;
      })
      .addCase(getMessageReadTimeThunk.fulfilled, (state, action) => {
        const { userId, createdAt } = action.payload;
        const message = state.messages.find(
          (m) => m.id === action.meta.arg.message.id
        );
        if (message) {
          const readByEntry = message.readBy.find((u) => u.userId === userId);
          if (readByEntry) {
            readByEntry.readAt = createdAt;
          }
        }
      })
      .addCase('chat/getVideoChatSession/fulfilled', (state, action) => {
        state.videoChatSession = action.payload.session;
      })
      .addCase('chat/loadUsers/fulfilled', (state, action) => {
        const { addToCurrentUsers } = action.meta.arg;
        if (addToCurrentUsers) {
          state.users = { ...state.users, ...action.payload };
        }
        state.allUsers = { ...state.allUsers, ...action.payload };
      })
      .addCase('chat/getProjectUsers/fulfilled', (state, action) => {
        const { addToCurrentUsers } = action.meta.arg;
        if (addToCurrentUsers) {
          state.users = action.payload;
        }
        state.projectUsers = action.payload;
        state.allUsers = { ...state.allUsers, ...action.payload };
      })
      .addCase('chat/updateLastReadMessage/fulfilled', (state, action) => {
        state.unreadChatMessagesCount = 0;
      })
      .addCase('chat/getProjectSubthreads/fulfilled', (state, action) => {
        const subThreads = action.payload;
        state.subThreads = subThreads;
        for (const subThread of subThreads) {
          state.threads[subThread.threadId || subThread.id] = subThread;
        }
      })
      .addCase('chat/addUsersToThread/fulfilled', (state, action) => {
        const updatedThread = action.payload;
        state.threads[updatedThread.threadId || updatedThread.id] =
          updatedThread;
        if (state.thread?.id === updatedThread.id) {
          state.thread = updatedThread;
          const filteredUsers = {};
          for (const userId of updatedThread.userIds) {
            if (state.allUsers[userId]) {
              filteredUsers[userId] = state.allUsers[userId];
            }
          }
          state.users = filteredUsers;
        }
      })
      .addCase('chat/getThread/fulfilled', (state, action) => {
        const thread = action.payload;
        state.threads[thread.threadId || thread.id] = thread;
        if (state.thread?.threadId === thread.id) {
          state.thread = thread;
        }
      })
      .addCase('chat/getSubthread/fulfilled', (state, action) => {
        const thread = action.payload;
        state.threads[thread.threadId || thread.id] = thread;
        if (state.thread?.threadId === thread.id) {
          state.thread = thread;
        }
      });
  },
});

// ACTIONS
export const {
  addMessagesToStart,
  addMessage,
  setAllMessagesFetched,
  setLastEvaluatedKey,
  setProject,
  setThread,
  setOpenedAttachment,
  setChatDialogOpen,
  setChatWebsocket,
  setVideoChatSession,
  setNewVideoChatSessionStarted,
  setVideoChatOpen,
  setAllMessagesRead,
  setNotifyMessage,
  addSubThread,
} = chatSlice.actions;

// SELECTORS
export const getChatControlData = (state) => {
  return state.chatSlice;
};
